import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PasswordResetService } from '../../services/password-reset.service';
import { AuthService } from '../../services/auth.service';
import { CustomFooterComponent } from '../custom-footer/custom-footer.component';

/**
 * Pagina pubblica raggiunta dal link inviato per email: chiunque abbia il token puo' impostare la
 * nuova password, anche chi non ha accesso alla Console. Nessuna guard, nessun layout autenticato.
 */
@Component({
  selector: 'app-password-reset',
  standalone: true,
  imports: [CommonModule, FormsModule, CustomFooterComponent, TranslateModule],
  templateUrl: './password-reset.component.html',
  styleUrls: ['./password-reset.component.scss']
})
export class PasswordResetComponent implements OnInit {
  password = '';
  confirmPassword = '';
  isLoading = false;
  message = '';
  messageType: 'success' | 'error' | '' = '';
  hasToken = false;

  private token = '';

  constructor(
    private readonly passwordResetService: PasswordResetService,
    private readonly authService: AuthService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.token = (this.route.snapshot.queryParamMap.get('token') || '').trim();
    this.hasToken = Boolean(this.token);
    if (!this.hasToken) {
      this.setMessage('error', this.translate.instant('passwordReset.messages.missingToken'));
    }
  }

  async submit(event?: Event): Promise<void> {
    event?.preventDefault();
    if (this.isLoading || !this.hasToken) {
      return;
    }

    if (!this.password || !this.confirmPassword) {
      this.setMessage('error', this.translate.instant('passwordReset.messages.allFieldsRequired'));
      return;
    }
    if (this.password !== this.confirmPassword) {
      this.setMessage('error', this.translate.instant('passwordReset.messages.passwordMismatch'));
      return;
    }
    if (this.password.length < 8) {
      this.setMessage('error', this.translate.instant('passwordReset.messages.passwordTooShort'));
      return;
    }

    this.isLoading = true;
    this.setMessage('', '');

    try {
      const encryptedPassword = await this.passwordResetService.encryptPassword(this.password);
      const result = await firstValueFrom(
        this.passwordResetService.resetPassword(this.token, encryptedPassword)
      );

      if (result === 'Done') {
        this.setMessage('success', this.translate.instant('passwordReset.messages.success'));
        // La sessione eventualmente aperta si basa sulla password vecchia: la chiudiamo, altrimenti
        // LoginComponent rimbalzerebbe subito l'utente ancora autenticato su /home.
        if (this.authService.currentUserValue) {
          this.authService.logout().subscribe({ error: () => { /* la sessione locale e' gia' ripulita */ } });
        }
        setTimeout(() => {
          this.router.navigate(['/login']);
        }, 2500);
      } else {
        // Il backend non distingue token scaduto da token inesistente o gia' usato.
        this.setMessage('error', this.translate.instant('passwordReset.messages.invalidToken'));
      }
    } catch (error: unknown) {
      // I messaggi del servizio sono chiavi i18n; quelli del backend restano testo grezzo.
      const raw = error instanceof Error ? error.message : '';
      const key = raw || 'passwordReset.messages.genericError';
      const translated = this.translate.instant(key);
      this.setMessage('error', translated === key && !key.startsWith('passwordReset.') ? raw : translated);
    } finally {
      this.isLoading = false;
    }
  }

  goToLogin(event?: Event): void {
    event?.preventDefault();
    this.router.navigate(['/login']);
  }

  private setMessage(type: 'success' | 'error' | '', message: string): void {
    this.messageType = type;
    this.message = message;
  }
}
